import { useForm } from '@mantine/form'
import React, { useState } from 'react'
import type { Key } from 'react-aria-components'
import { ListBox, ListBoxItem } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { cn, cx } from 'src/utils/cx'
import MediaPreview from '../Common/MediaPreview'
import { Preloader } from '../Preloader/Preloader'
import { UIFormLabel, UIFormTextField } from '../ui/Form'
import { UIInput } from '../ui/TextField'
import { TagChipGroup } from './TagChip'
import { useSearchTag } from './useSearchTag'
import type { TagItem } from './utils'
import { getTagCategory, getTagName } from './utils'

export type TagSelectProps = {
  value: TagItem[]
  label?: React.ReactNode
  onTagSelected: (tag: TagItem) => void
  onTagRemove: (tag: TagItem) => void
}

export const TagSelect = ({
  value,
  label,
  onTagSelected,
  onTagRemove,
}: TagSelectProps) => {
  const { t } = useTranslation(['artwork'])

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { onInputValueChange, tags } = useSearchTag()

  const form = useForm({
    initialValues: {
      input: '',
    },

    onValuesChange: values => {
      onInputValueChange(values.input)
    },
  })

  const onListBoxAction = (key: Key) => {
    const tag = tags.find(tag => tag.id === key)
    if (!tag) return

    setIsPopoverOpen(false)
    onTagSelected(tag)
    form.setFieldValue('input', '')
  }

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()

      const tagString = form.getValues().input
      const tagNames = tagString
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0)

      for (const tagName of tagNames) {
        const existingTag = tags.find(tag => tag.name === tagName)
        if (existingTag) {
          onTagSelected(existingTag)
        } else {
          // only allow selecting from the options list
          // Do not allow adding custom string tags
        }
      }

      form.setFieldValue('input', '')
    }
  }

  return (
    <div className="relative">
      <UIFormTextField layout="vertical" input={form.getInputProps('input')}>
        {input => (
          <>
            <UIFormLabel>{label}</UIFormLabel>

            <TagChipGroup
              chips={value.map(tag => ({
                tag: {
                  name: getTagName(tag),
                  tag: tag,
                },
                type: 'remove',
                onAdd: onTagSelected,
                onRemove: onTagRemove,
                variant: 'selection',
                category: getTagCategory(tag),
              }))}
              size="medium"
            />

            <UIInput
              {...input}
              variant="rounded-normal"
              placeholder={t('artwork:tag-placeholder')}
              onFocus={() => setIsPopoverOpen(true)}
              onKeyDownCapture={onInputKeyDown}
            />
          </>
        )}
      </UIFormTextField>

      {/* Menu Popover */}
      {isPopoverOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsPopoverOpen(false)}
          />
          <div
            className={cx(
              'absolute top-full left-0 right-0 z-20 mt-1',
              'transform transition-all duration-200 ease-out',
              'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2',
            )}
          >
            <ListBox
              items={tags}
              className={cx(
                'outline-none rounded-lg shadow-lg overflow-y-auto border flex flex-col max-h-80',
                'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700',
              )}
              onAction={onListBoxAction}
              renderEmptyState={() => <Preloader />}
            >
              {item => (
                <ListBoxItem
                  key={item.id}
                  className={cn(
                    'flex items-center gap-2 text-base cursor-pointer',
                    'px-4 py-2',
                    'hover:bg-neutral-200 dark:hover:bg-neutral-700',
                    'text-gray-900 dark:text-gray-100',
                  )}
                >
                  {item.mediaId && (
                    <MediaPreview
                      className="w-6 h-6 rounded-full"
                      mediaId={item.mediaId}
                    />
                  )}
                  <span>{item.displayName ?? item.name}</span>
                </ListBoxItem>
              )}
            </ListBox>
          </div>
        </>
      )}
    </div>
  )
}
